<template>
  <div class="container my-5">
    <div class="row">
      <div class="col-md-6 mb-4">
        <div class="card card-widget h-100">
          <div class="card-body">
            <h3 class="widget-title">{{ budget.billing_type == 'Post' ? 'Unbilled' : 'Current Balance' }}</h3>
            <p class="lead mb-0">{{ budget.balance }} <small class="text-muted">hrs</small></p>
          </div>
        </div>
      </div>
      <div class="col-md-6 mb-4">
        <div class="card card-widget h-100">
          <div class="card-body">
            <h3 class="widget-title">Total Hours Spent</h3>
            <p class="lead">{{ budget.spent }} <small class="text-muted">of {{ budget.budget }} hrs</small></p>
            <div class="progress">
              <div
                class="progress-bar"
                :class="{ 'bg-danger': usagePercentage >= 100 }"
                role="progressbar"
                :style="{ width: Math.min(usagePercentage, 100) + '%' }"
                :aria-valuenow="usagePercentage"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col d-flex align-items-center">
            <h2 class="card-title text-truncate mb-0">{{ budget.name }}</h2>
          </div>
          <div class="col-auto">
            <button
              type="button"
              class="btn btn-sm btn-link"
              v-c-modal:budgetModal="{}"
            >
              <i class="far fa-pencil-alt"></i>
              Edit
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 col-lg-3">
            <p>
              <span class="data-label">Billing Type</span>
              {{ budget.billing_type }}
            </p>
          </div>
          <div class="col-sm-6 col-lg-3">
            <p>
              <span class="data-label">Start Date</span>
              <template v-if="budget.start_date">{{ budget.start_date | moment('DD/MM/YYYY') }}</template>
              <template v-else>-</template>
            </p>
          </div>
          <div class="col-sm-6 col-lg-3">
            <p>
              <span class="data-label">Owner</span>
              {{ budget.user ? budget.user.name : '-' }}
            </p>
          </div>
          <div class="col-sm-6 col-lg-3">
            <p>
              <span class="data-label">Linked Opportunity</span>
              <router-link
                v-if="budget.opportunity"
                :to="{ name: 'opportunities.overview', params: { opportunity_id: budget.opportunity.id } }"
              >
                {{ budget.opportunity.formatted_name || budget.opportunity.name }}
              </router-link>
              <template v-else>-</template>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h2 class="card-title mb-3">Client Details</h2>
        <div class="row">
          <div class="col-sm-6 col-lg-3">
            <p class="text-truncate">
              <span class="data-label">Client Name</span>
              <router-link :to="{ name: 'clients.show', params: { client_id: budget.client_id } }">
                {{ client.company }}
              </router-link>
            </p>
          </div>
          <div class="col-sm-6 col-lg-3">
            <p class="text-truncate">
              <span class="data-label">Contact Name</span>
              <template v-if="budget.contact">{{ budget.contact.first_name }} {{ budget.contact.surname }}</template>
            </p>
          </div>
          <div class="col-sm-6 col-lg-3">
            <p class="text-truncate">
              <span class="data-label">Contact Email</span>
              <template v-if="budget.contact">{{ budget.contact.email }}</template>
            </p>
          </div>
          <div class="col-sm-6 col-lg-3">
            <p>
              <span class="data-label">Contact Phone</span>
              <template v-if="budget.contact">
                {{ budget.contact.phone }}<br v-if="budget.contact.phone && budget.contact.mobile">
                {{ budget.contact.mobile }}
              </template>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col d-flex align-items-center">
        <h2 class="mb-0">Approvals</h2>
      </div>
      <div class="col-auto">
        <button
          type="button"
          class="btn btn-primary"
          v-c-modal:approvalModal="{ approval: {} }"
        >
          <i class="far fa-plus"></i> Approval
        </button>
      </div>
    </div>

    <c-data-list :items="budget.approvals || []" class="no-links mb-4">
      <template v-slot:columns>
        <div class="col-6 col-lg-2">
          <p>Date</p>
        </div>
        <div class="col-6 col-lg-2">
          <p>Amount</p>
        </div>
        <div class="col-12 col-lg-2">
          <p>Recorded By</p>
        </div>
        <div class="col-12 col-lg-5">
          <p>Comment</p>
        </div>
        <div class="col-12 col-lg-1"></div>
      </template>
      <template v-slot:default="{ item }">
        <div class="row">
          <div class="col-6 col-lg-2">
            <p>
              <span class="data-label d-lg-none">Date:</span>
              {{ item.date | moment('DD/MM/YYYY') }}
            </p>
          </div>
          <div class="col-6 col-lg-2">
            <p>
              <span class="data-label d-lg-none">Amount:</span>
              {{ item.amount }} hrs
            </p>
          </div>
          <div class="col-12 col-lg-2">
            <p>
              <span class="data-label d-lg-none">Recorded By:</span>
              {{ item.user ? item.user.name : '-' }}
            </p>
          </div>
          <div class="col-12 col-lg-5">
            <p>{{ item.note }}</p>
          </div>
          <div class="col-12 col-lg-1 text-lg-end">
            <button type="button" class="btn btn-sm btn-link" v-c-modal:approvalModal="{ approval: item }">
              <i class="far fa-pencil-alt"></i>
            </button>
            <button type="button" class="btn btn-sm btn-link text-danger" @click="confirmDeleteApproval(item)">
              <i class="far fa-trash"></i>
            </button>
          </div>
        </div>
      </template>
      <template v-slot:empty>
        <li class="py-4">
          <p class="text-center">No approvals recorded.</p>
        </li>
      </template>
    </c-data-list>

    <div class="card mb-4">
      <div class="card-body">
        <h2 class="card-title mb-3">Notes</h2>
        <wysiwyg-inline :content="budget.description" :save="saveDescription"></wysiwyg-inline>
      </div>
    </div>

    <a href="#" class="text-danger" @click.prevent="confirmArchive">Archive Budget</a>

    <budget-modal
      ref="budgetModal"
      title="Edit Time & Material Project"
      :budget="budget"
      @updated="budgetUpdated"
    />
    <client-create-modal
      ref="clientModal"
      title="Create Client"
      :client="{}"
      mode="create"
    />
    <contact-create-modal
      ref="contactModal"
      title="Create Contact"
      :contact="{}"
    />
    <approval-modal ref="approvalModal" @saved="reloadBudget" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { WysiwygInline } from 'components/form/input'
import { helperComputed, helperMethods } from 'vx/modules/clients'
import BudgetModal from './BudgetModal'
import ApprovalModal from './ApprovalModal'
import ClientCreateModal from 'components/clients/ClientCreateModal'
import ContactCreateModal from 'components/clients/client/ContactCreateModal'

export default {
  components: {
    BudgetModal,
    ApprovalModal,
    ClientCreateModal,
    ContactCreateModal,
    WysiwygInline
  },

  computed: {
    ...helperComputed,
    ...mapState('budget', {
      budget: 'single'
    }),

    usagePercentage () {
      if (!this.budget.budget) {
        return 0
      }

      return Math.round(this.budget.spent / this.budget.budget * 100)
    }
  },

  methods: {
    ...helperMethods,
    ...mapActions('budget', {
      updateSingle: 'updateSingle',
      deleteSingle: 'deleteSingle'
    }),
    ...mapActions('budget/approvals', {
      deleteApproval: 'deleteSingle'
    }),

    reloadBudget () {
      this.$bus.$emit('budget-reload-data')
    },

    budgetUpdated (budget) {
      this.setClient(budget.client)
    },

    saveDescription (data) {
      return this.updateSingle({ id: this.budget.id, payload: { description: data } })
    },

    confirmArchive () {
      this.$swal({
        title: 'Confirm Archive',
        html: 'Are you sure you want to archive this budget',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Archive'
      }).then(() => {
        this.deleteSingle(this.budget.id).then(() => this.$router.push({ name: 'budgets.index' }))
      }, () => {})
    },

    confirmDeleteApproval (approval) {
      this.$swal({
        title: 'Confirm Delete',
        html: 'Are you sure you want to delete this approval',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Delete'
      }).then(() => {
        this.deleteApproval(approval.id).then(this.reloadBudget)
      }, () => {})
    }
  }
}
</script>
